import React, { useRef, useState, useEffect } from 'react'
import fixWebmDuration from 'webm-duration-fix'

interface VideoPlayerProps {
  src: string
}

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '--:--'
  }
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts = [
    h > 0 ? String(h).padStart(2, '0') : null,
    String(m).padStart(2, '0'),
    String(s).padStart(2, '0'),
  ].filter(Boolean)
  return parts.join(':')
}

const VideoPlayer = ({ src }: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [speed, setSpeed] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number>(NaN)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)


  useEffect(() => {
    let urlToRevoke: string | null = null
    let isMounted = true

    ;(async () => {
      setLoading(true)
      try {
        const res = await fetch(src, { mode: 'cors', credentials: 'omit' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const original = await res.blob()

        // Try to fix duration if missing/infinite
        const fixed = await fixWebmDuration(original)
        const url = URL.createObjectURL(fixed)
        urlToRevoke = url
        if (isMounted) setBlobUrl(url)
      } catch (e) {
        console.error('Video fetch/fix failed', e)
      } finally {
        if (isMounted) setLoading(false)
      }
    })()

    return () => {
      isMounted = false
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke)
    }
  }, [src])

  const updateDuration = () => {
    const v = videoRef.current
    if (!v) return false

    let d = v.duration
    // if duration is NaN or Infinity, check buffered instead
    if (!Number.isFinite(d) && v.buffered.length > 0) {
      d = v.buffered.end(v.buffered.length - 1)
    }
    if (Number.isFinite(d)) {
      setDuration(d)
      return true
    }
    return false
  }

  // On metadata load, attempt to set duration immediately
  const handleLoadedMetadata = () => {
    updateDuration()
  }

  // Poll once (after a short delay) if we still don't have a duration
  useEffect(() => {
    if (!Number.isFinite(duration)) {
      const timer = setTimeout(() => {
        updateDuration()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [duration])

  return (
    <div className="space-y-2">
      {loading && <div className="text-center text-gray-500">Video indiriliyor...</div>}
      {!loading && blobUrl && (
        <video
          ref={videoRef}
          src={blobUrl}
          controls
          preload="auto"
          crossOrigin="anonymous" // needed so JS can read duration
          onLoadedMetadata={updateDuration}
          onDurationChange={updateDuration}
          onCanPlayThrough={updateDuration}
          onTimeUpdate={() => {
            if (videoRef.current) setCurrentTime(videoRef.current.currentTime)
          }}
          style={{ width: '100%', borderRadius: 8 }}
        />
      )}

      {/* Always show “current / total” */}
      {/* <div className="flex justify-end text-sm text-gray-500">
     {formatTime(currentTime)} / {formatTime(duration)}
   </div> */}

      <div className="flex gap-2">
        {[1, 1.5, 2].map((rate) => (
          <button
            key={rate}
            className={`px-3 py-1 rounded ${
              speed === rate ? 'bg-blue-600 text-white' : 'bg-gray-200'
            }`}
            onClick={() => {
              if (videoRef.current) {
                videoRef.current.playbackRate = rate
                setSpeed(rate)
              }
            }}
          >
            {rate}×
          </button>
        ))}
      </div>
    </div>
  )
}

export default VideoPlayer
